import type { VariableDeclaration } from '../../types/agent.js';
import type { VariableRow } from '../AgentsPage/AgentForm/AgentForm.types.js';
import { VARIABLE_NAME_PATTERN } from '../AgentsPage/AgentForm/AgentForm.types.js';
import {
  isVariableRowEmpty,
  makeRowId,
} from '../AgentsPage/AgentForm/AgentForm.utils.js';

function defaultToInput(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  return JSON.stringify(value);
}

export function variableRowsFromMap(
  variables: Record<string, VariableDeclaration> | undefined,
): VariableRow[] {
  return Object.entries(variables ?? {}).map(([name, declaration]) => ({
    id: makeRowId('grader-var'),
    name,
    type: declaration.type,
    defaultValue: defaultToInput(declaration.default),
    description: declaration.description ?? '',
  }));
}

export function validateVariableRows(
  rows: VariableRow[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  const names = new Set<string>();
  for (const row of rows) {
    if (isVariableRowEmpty(row)) continue;
    const name = row.name.trim();
    if (name.length === 0) {
      errors[row.id] = 'Name is required.';
    } else if (!VARIABLE_NAME_PATTERN.test(name)) {
      errors[row.id] = 'Use UPPER_SNAKE_CASE.';
    } else if (names.has(name)) {
      errors[row.id] = 'Duplicate variable name.';
    }
    names.add(name);
  }
  return errors;
}

export function variableMapsEqual(
  left: Record<string, VariableDeclaration> | undefined,
  right: Record<string, VariableDeclaration> | undefined,
): boolean {
  const leftEntries = Object.entries(left ?? {});
  const rightMap = right ?? {};
  if (leftEntries.length !== Object.keys(rightMap).length) return false;

  return leftEntries.every(([name, declaration]) => {
    const other = rightMap[name];
    return (
      declaration.type === other?.type &&
      declaration.description === other.description &&
      JSON.stringify(declaration.default) === JSON.stringify(other.default)
    );
  });
}
